import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  ArrowLeft, ArrowRight, Skull, Ghost, Loader2, CheckCircle2, XCircle,
  Copy, Eye, EyeOff, Mail, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

/*  Halloween Teaser Vault — pick one of three free mini mysteries, play,
    then drop an email to unlock the shared TEASER10 code (10% off any Vault
    purchase).  Routes:  /teaser    */

const Teaser = () => {
  const [lobby, setLobby] = useState(null);
  const [picked, setPicked] = useState(null);          // mystery_id selected
  const [mystery, setMystery] = useState(null);        // playable payload
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/teaser/mysteries").then((r) => setLobby(r.data))
      .catch(() => toast.error("Could not load the teaser vault."))
      .finally(() => setLoading(false));
  }, []);

  const pick = async (id) => {
    setPicked(id);
    setMystery(null);
    try {
      const { data } = await api.get(`/teaser/mystery/${id}`);
      setMystery(data.mystery);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Could not load that mystery.");
      setPicked(null);
    }
  };

  const back = () => {
    setPicked(null);
    setMystery(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading) {
    return (
      <div className="grain min-h-screen flex items-center justify-center">
        <Loader2 size={28} className="animate-spin text-[#d4af37]" />
      </div>
    );
  }

  return (
    <div className="grain min-h-screen" data-testid="teaser-page">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#a39797] hover:text-[#d4af37] mb-6">
          <ArrowLeft size={16} /> back to the file
        </Link>

        {!picked ? (
          <TeaserLobby lobby={lobby} onPick={pick} />
        ) : !mystery ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={28} className="animate-spin text-[#d4af37]" />
          </div>
        ) : (
          <TeaserPlayer mystery={mystery} onBack={back} />
        )}
      </div>
    </div>
  );
};

/* ---------- Lobby ---------- */

const TeaserLobby = ({ lobby, onPick }) => {
  const mysteries = lobby?.mysteries || [];
  const code = lobby?.coupon_code || "TEASER10";
  const pct = lobby?.discount_pct || 10;
  return (
    <>
      <div className="text-center mb-10">
        <div className="font-hand text-[#d4af37] rotate-[-1deg] text-lg mb-3">free · all hallows' play</div>
        <h1 className="font-display text-5xl sm:text-6xl text-[#f4f0ec] mb-4">The Teaser Vault</h1>
        <p className="text-[#c7bebe] text-lg max-w-xl mx-auto">
          Three bite-sized Halloween murders. Pick one, solve it, unlock
          <span className="text-[#d4af37] font-bold" translate="no"> {pct}% off </span>
          every case in the Vault. Code: <span className="font-mono text-[#d4af37]" translate="no">{code}</span>.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="teaser-lobby-grid">
        {mysteries.map((m) => (
          <button key={m.id} onClick={() => onPick(m.id)}
            className="card-noir p-5 text-left hover:border-[#d4af37] transition-colors group"
            data-testid={`teaser-pick-${m.id}`}>
            <Ghost size={18} className="text-[#d4af37] mb-3 group-hover:scale-110 transition-transform" />
            <div className="font-display text-xl text-[#f4f0ec] mb-2">{m.title}</div>
            <div className="text-sm text-[#a39797] mb-4 min-h-[48px]">{m.tagline}</div>
            <div className="flex items-center justify-between text-[11px] text-[#5a5050]">
              <span>{m.suspect_count} suspects · {m.clue_count} clues</span>
              <ArrowRight size={14} className="text-[#d4af37]" />
            </div>
          </button>
        ))}
      </div>

      <div className="card-noir p-5 mt-8 text-center">
        <div className="text-xs tracking-widest text-[#a39797] mb-1">AFTER YOU PLAY</div>
        <div className="font-display text-xl text-[#f4f0ec]">
          Drop your email, get a <span className="text-[#d4af37]">{pct}%</span> code for every Vault case.
        </div>
        <div className="text-xs text-[#5a5050] mt-2">
          No spam. One welcome email, then only new case drops.
        </div>
      </div>
    </>
  );
};

/* ---------- Player ---------- */

const TeaserPlayer = ({ mystery, onBack }) => {
  const [openClueIdx, setOpenClueIdx] = useState(null);
  const [guessId, setGuessId] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { correct, culprit, reveal, coupon_code }
  const [copied, setCopied] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!guessId) { toast.error("Pick a suspect first."); return; }
    if (!email) { toast.error("Email required to unlock the code."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/teaser/guess", {
        mystery_id: mystery.id, guess_id: guessId,
        email, name, marketing_opt_in: true,
      });
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(result.coupon_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Could not copy.");
    }
  };

  /* --- Result screen (post-guess) --- */
  if (result) {
    return (
      <div data-testid="teaser-result">
        <button onClick={onBack} className="btn-ghost text-sm mb-6" data-testid="teaser-back-to-lobby">
          <ArrowLeft size={14} /> Try another
        </button>

        <div className={`card-noir p-6 mb-5 ${result.correct ? "border-[#4ade80]" : "border-[#ff6b6b]"}`}
          data-testid={result.correct ? "teaser-correct" : "teaser-incorrect"}>
          <div className="flex items-center gap-2 mb-2">
            {result.correct ? (
              <><CheckCircle2 size={20} className="text-[#4ade80]" />
                <div className="font-display text-2xl text-[#f4f0ec]">You caught them.</div></>
            ) : (
              <><XCircle size={20} className="text-[#ff6b6b]" />
                <div className="font-display text-2xl text-[#f4f0ec]">Not quite.</div></>
            )}
          </div>
          <div className="text-sm text-[#c7bebe] mb-3">
            It was <span className="text-[#d4af37] font-bold" translate="no">{result.culprit?.name}</span>
            {result.culprit?.costume ? <> — the {result.culprit.costume.toLowerCase()}.</> : "."}
          </div>
          <div className="text-sm text-[#a39797] leading-relaxed border-t border-[#2d2424] pt-3 mt-3">
            {result.reveal}
          </div>
        </div>

        <div className="card-noir p-6 mb-5 border-[#d4af37]" data-testid="teaser-coupon-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs tracking-widest text-[#a39797]">UNLOCKED · {result.discount_pct}% OFF ANY VAULT CASE</div>
              <div className="font-display text-4xl text-[#d4af37] mt-2 tracking-widest" translate="no" data-testid="teaser-coupon-code">
                {result.coupon_code}
              </div>
            </div>
            <button onClick={copyCode} className="btn-ghost text-sm" data-testid="teaser-copy-code">
              {copied ? <CheckCircle2 size={14} className="text-green-400" /> : <Copy size={14} />}
              {copied ? "Copied" : "Copy code"}
            </button>
          </div>
          <div className="text-xs text-[#a39797] mt-3">
            Paste it at checkout on any case, Party Pack, Case Maker build, or Crew Night upgrade.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Link to="/" className="btn-primary justify-center" data-testid="teaser-cta-vault">
            Browse the Vault <ArrowRight size={14} />
          </Link>
          <Link to="/club" className="btn-ghost justify-center" data-testid="teaser-cta-club">
            Or join the Club · $9/mo <Sparkles size={14} />
          </Link>
        </div>
      </div>
    );
  }

  /* --- Play screen --- */
  return (
    <div data-testid="teaser-play">
      <button onClick={onBack} className="btn-ghost text-sm mb-6" data-testid="teaser-back-btn">
        <ArrowLeft size={14} /> Back to lobby
      </button>

      <div className="mb-6">
        <div className="font-hand text-[#d4af37] rotate-[-1deg] text-sm mb-2">{mystery.tagline}</div>
        <h1 className="font-display text-4xl text-[#f4f0ec] mb-4">{mystery.title}</h1>
        <p className="text-[#c7bebe] leading-relaxed">{mystery.scene}</p>
      </div>

      {/* Suspects */}
      <div className="mb-6">
        <div className="text-xs tracking-widest text-[#a39797] mb-3">SUSPECTS</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="teaser-suspects">
          {mystery.suspects.map((s) => (
            <button key={s.id} onClick={() => setGuessId(s.id)} type="button"
              className={`card-noir p-4 text-left transition-colors ${
                guessId === s.id ? "border-[#d4af37] bg-[#1a1413]" : "hover:border-[#8b0000]"
              }`}
              data-testid={`teaser-suspect-${s.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="font-display text-lg text-[#f4f0ec]" translate="no">{s.name}</div>
                {guessId === s.id && <CheckCircle2 size={16} className="text-[#d4af37]" />}
              </div>
              <div className="text-[11px] text-[#d4af37] mt-1">{s.costume}</div>
              <div className="text-xs text-[#a39797] mt-2">{s.role}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Clues */}
      <div className="mb-6">
        <div className="text-xs tracking-widest text-[#a39797] mb-3">CLUES — click to inspect</div>
        <div className="space-y-2" data-testid="teaser-clues">
          {mystery.clues.map((c, i) => {
            const open = openClueIdx === i;
            return (
              <button key={i} onClick={() => setOpenClueIdx(open ? null : i)} type="button"
                className="w-full card-noir p-4 text-left hover:border-[#d4af37] transition-colors"
                data-testid={`teaser-clue-${i}`}>
                <div className="flex items-center gap-3">
                  {open ? <Eye size={14} className="text-[#d4af37]" /> : <EyeOff size={14} className="text-[#5a5050]" />}
                  <div className="text-xs tracking-widest text-[#a39797]">CLUE {i + 1}</div>
                </div>
                {open && (
                  <div className="text-sm text-[#c7bebe] mt-2 leading-relaxed">{c}</div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Submit */}
      <form onSubmit={submit} className="card-noir p-6 border-[#d4af37]" data-testid="teaser-submit-card">
        <div className="font-display text-xl text-[#f4f0ec] mb-1">Make your accusation</div>
        <div className="text-xs text-[#a39797] mb-4">
          {guessId
            ? `You're accusing ${mystery.suspects.find((s) => s.id === guessId)?.name}. Drop your email to see the reveal + unlock 10% off.`
            : "Pick a suspect above, then enter your email."}
        </div>
        <input type="email" required placeholder="Your email"
          value={email} onChange={(e) => setEmail(e.target.value)}
          className="w-full bg-[#0a0908] border border-[#2d2424] rounded-md px-3 py-3 text-[#f4f0ec] text-sm mb-3 focus:outline-none focus:border-[#d4af37]"
          data-testid="teaser-email" />
        <input placeholder="Your name (optional)"
          value={name} onChange={(e) => setName(e.target.value)}
          className="w-full bg-[#0a0908] border border-[#2d2424] rounded-md px-3 py-3 text-[#f4f0ec] text-sm mb-4 focus:outline-none focus:border-[#d4af37]"
          data-testid="teaser-name" />
        <button type="submit" disabled={busy || !guessId}
          className="btn-primary w-full justify-center disabled:opacity-50"
          data-testid="teaser-submit-btn">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Skull size={14} />}
          Reveal the killer + unlock code
        </button>
        <div className="text-[11px] text-[#5a5050] mt-3 text-center flex items-center gap-1 justify-center">
          <Mail size={11} /> No spam, ever. Unsubscribe in one click.
        </div>
      </form>
    </div>
  );
};

export default Teaser;
